import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { billingApi } from '../../../services/billingApi';
import { currentUserHasCerebralAdminRole } from '../../../utils/roleCheck';
import { Alert, Button, Modal, Select, Textarea, TextInput } from '../../../components/common';
import { StatTile, StatusBadge, SectionCard, EmptyState, UsageChart } from './components';
import { formatCompactNumber, formatNumber, formatDate, formatDateOnly } from './format';
import './BillingAdmin.css';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'disabled', label: 'Disabled' },
  { value: 'suspended', label: 'Suspended' },
];

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'subscriptions', label: 'Subscriptions' },
  { id: 'audit', label: 'Audit' },
];

const KIND_TONE = { query: 'query', payload: 'payload', tokens: 'tokens' };

function CopyIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

function CopyableId({ value }) {
  const [copied, setCopied] = useState(false);
  if (!value) return <span className="billing-admin__muted">—</span>;
  const handleCopy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard?.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      /* ignore */
    }
  };
  return (
    <button type="button" className="billing-admin__id-pill" onClick={handleCopy} title="Copy installation ID">
      <CopyIcon />
      <span>{copied ? 'Copied!' : value}</span>
    </button>
  );
}

function tsKey(p) {
  return p.PeriodStart || p.period_start || p.Day || p.day || p.OccurredAt || p.occurred_at;
}

function buildSeries(rawSeries, kind) {
  const filtered = (rawSeries || []).filter((p) => {
    const k = (p.Kind || p.kind || '').toLowerCase();
    return k === kind;
  });
  return filtered
    .map((p) => ({ ts: tsKey(p), y: Number(p.SumQuantity || p.sum_quantity || p.quantity || 0) || 0 }))
    .sort((a, b) => new Date(a.ts).getTime() - new Date(b.ts).getTime());
}

function actionLabel(action) {
  if (!action) return '';
  return String(action)
    .replace(/[._]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function InstallationDetailPage() {
  const { installationId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [usage, setUsage] = useState(null);
  const [auditEvents, setAuditEvents] = useState(null);
  const [auditError, setAuditError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [chartKind, setChartKind] = useState('query');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusForm, setStatusForm] = useState({ status: 'disabled', reason: '', scheduledAt: '' });
  const [statusBusy, setStatusBusy] = useState(false);
  const [statusError, setStatusError] = useState(null);

  useEffect(() => {
    if (!currentUserHasCerebralAdminRole()) {
      navigate('/user-home');
    }
  }, [navigate]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [det, ts] = await Promise.all([
        billingApi.getInstallation(installationId),
        billingApi.getInstallationUsage(installationId, { granularity: 'day' }),
      ]);
      setData(det);
      setUsage(ts);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to load installation');
    } finally {
      setLoading(false);
    }
  }, [installationId]);

  useEffect(() => { load(); }, [load]);

  // Lazy-load audit when its tab is opened.
  useEffect(() => {
    if (activeTab !== 'audit' || auditEvents !== null) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await billingApi.getInstallationAudit(installationId);
        if (!cancelled) setAuditEvents(r?.events || r?.items || []);
      } catch (e) {
        if (!cancelled) setAuditError(e?.response?.data?.message || e.message || 'Failed to load audit log');
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, auditEvents, installationId]);

  const inst = data?.installation || {};
  const subs = data?.subscriptions || [];
  const summary = data?.currentPeriod?.summary || {};
  const perAccount = data?.currentPeriod?.perAccount || [];

  const chartSeries = useMemo(() => buildSeries(usage?.series || [], chartKind), [usage, chartKind]);
  const chartTotal = chartSeries.reduce((s, p) => s + p.y, 0);

  const status = (inst.Status || inst.status || 'active').toLowerCase();

  const submitStatus = async () => {
    if (!statusForm.status) return;
    setStatusBusy(true);
    setStatusError(null);
    try {
      await billingApi.setInstallationStatus(installationId, {
        status: statusForm.status,
        reason: statusForm.reason || undefined,
        scheduledAt: statusForm.scheduledAt || undefined,
      });
      setShowStatusModal(false);
      setStatusForm({ status: 'disabled', reason: '', scheduledAt: '' });
      await load();
    } catch (e) {
      setStatusError(e?.response?.data?.message || e.message || 'Failed to update status');
    } finally {
      setStatusBusy(false);
    }
  };

  if (error) {
    return (
      <div className="user-home-layout__page">
        <Link to="/user-home/admin/billing" className="billing-admin__breadcrumb">← Back to dashboard</Link>
        <Alert variant="error" title="Unable to load installation">{error}</Alert>
      </div>
    );
  }

  return (
    <div className="user-home-layout__page">
      <Link to="/user-home/admin/billing" className="billing-admin__breadcrumb">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
        Back to dashboard
      </Link>

      <div className="billing-admin__page-header">
        <div className="billing-admin__header-text">
          <h1 className="user-home-layout__page-title" style={{ marginBottom: 8 }}>
            {loading ? <span className="billing-admin__skeleton billing-admin__skeleton--lg" style={{ width: 240 }} /> : (inst.Label || inst.label || '(no label)')}
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            {!loading && <StatusBadge status={status} />}
            {!loading && (inst.Environment || inst.environment) && (
              <span className="billing-admin__kind-chip">{inst.Environment || inst.environment}</span>
            )}
            <CopyableId value={inst.InstallationId || installationId} />
          </div>
        </div>
        <div className="billing-admin__header-actions">
          {!loading && (
            <Button
              variant={status === 'active' ? 'danger' : 'primary'}
              onClick={() => {
                setStatusForm({
                  status: status === 'active' ? 'disabled' : 'active',
                  reason: '',
                  scheduledAt: '',
                });
                setStatusError(null);
                setShowStatusModal(true);
              }}
            >
              {status === 'active' ? 'Disable installation' : 'Enable installation'}
            </Button>
          )}
        </div>
      </div>

      <div className="billing-admin__tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`billing-admin__tab ${activeTab === t.id ? 'is-active' : ''}`}
            onClick={() => setActiveTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Overview */}
      {activeTab === 'overview' && (
        <>
          <div className="billing-admin__stats">
            <StatTile
              tone="active"
              label="Status"
              value={loading ? <span className="billing-admin__skeleton billing-admin__skeleton--lg" /> : <StatusBadge status={status} />}
            />
            <StatTile
              label="Report interval"
              value={loading ? <span className="billing-admin__skeleton billing-admin__skeleton--lg" /> : `${formatNumber(inst.ReportIntervalSeconds || 3600)}s`}
              sub="Customer cloud heartbeat cadence"
            />
            <StatTile
              label="Last heartbeat"
              value={loading ? <span className="billing-admin__skeleton billing-admin__skeleton--lg" /> : formatDate(inst.LastHeartbeatAt) || '—'}
              sub={inst.LastHeartbeatAt ? '' : 'Never reported'}
            />
            <StatTile
              label="Last ingest seq"
              value={loading ? <span className="billing-admin__skeleton billing-admin__skeleton--lg" /> : formatNumber(inst.LastIngestSeq ?? 0)}
              sub="Monotonic sequence counter"
            />
          </div>

          <SectionCard
            title="Current billing period"
            subtitle="Aggregated usage across every account on this installation."
          >
            <div className="billing-admin__stats" style={{ marginBottom: 0 }}>
              <StatTile
                tone="query"
                label="Queries"
                value={loading ? <span className="billing-admin__skeleton billing-admin__skeleton--lg" /> : formatCompactNumber(summary.query?.quantity ?? summary.totals?.query ?? 0)}
              />
              <StatTile
                tone="payload"
                label="Payload (MB)"
                value={loading ? <span className="billing-admin__skeleton billing-admin__skeleton--lg" /> : formatCompactNumber(Math.round((summary.payload?.quantity ?? summary.totals?.payload ?? 0) / 1024 / 1024))}
              />
              <StatTile
                tone="tokens"
                label="Tokens"
                value={loading ? <span className="billing-admin__skeleton billing-admin__skeleton--lg" /> : formatCompactNumber(summary.tokens?.quantity ?? summary.totals?.tokens ?? 0)}
              />
              <StatTile
                label="Accounts billed"
                value={loading ? <span className="billing-admin__skeleton billing-admin__skeleton--lg" /> : formatNumber(perAccount.length)}
                sub="Distinct accounts with activity"
              />
            </div>
          </SectionCard>

          <SectionCard
            title="Usage timeseries"
            subtitle="Last 30 days, grouped by billable kind."
            toolbar={
              <div className="billing-admin__segmented" role="tablist" aria-label="Chart kind">
                {[
                  { id: 'query', label: 'Queries' },
                  { id: 'payload', label: 'Payload' },
                  { id: 'tokens', label: 'Tokens' },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    className={`billing-admin__segmented-btn ${chartKind === opt.id ? 'is-active' : ''}`}
                    onClick={() => setChartKind(opt.id)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            }
          >
            <div className="billing-admin__chart-header">
              <div>
                <p className="billing-admin__stat-label" style={{ marginBottom: 4 }}>
                  Period total
                </p>
                <p className="billing-admin__chart-total">
                  {loading ? <span className="billing-admin__skeleton billing-admin__skeleton--lg" style={{ width: 120 }} /> : formatCompactNumber(chartTotal)}
                </p>
              </div>
            </div>
            {loading ? (
              <div className="billing-admin__skeleton" style={{ height: 200, borderRadius: 8 }} />
            ) : (
              <UsageChart
                data={chartSeries}
                tone={KIND_TONE[chartKind] || 'query'}
                yLabel={chartKind === 'payload' ? 'bytes' : chartKind}
              />
            )}
          </SectionCard>

          <SectionCard title="Per-account usage (current period)" flush>
            <div className="billing-admin__table-wrap">
              <table className="billing-admin__table">
                <thead>
                  <tr>
                    <th>Account</th>
                    <th className="num">Queries</th>
                    <th className="num">Payload bytes</th>
                    <th className="num">Tokens</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && Array.from({ length: 3 }).map((_, idx) => (
                    <tr key={`sk-${idx}`} className="billing-admin__skeleton-row">
                      {Array.from({ length: 4 }).map((__, c) => (
                        <td key={c}><span className="billing-admin__skeleton" /></td>
                      ))}
                    </tr>
                  ))}
                  {!loading && perAccount.length === 0 && (
                    <tr>
                      <td colSpan={4} style={{ padding: 0, border: 0 }}>
                        <EmptyState title="No usage in this period yet" message="Once the customer cloud reports usage events, per-account aggregates will appear here." />
                      </td>
                    </tr>
                  )}
                  {!loading && perAccount.map((row, idx) => (
                    <tr key={idx}>
                      <td className="billing-admin__table-cell-primary">{row.AccountName || row.CompanyAccountId || '(unknown)'}</td>
                      <td className="num">{formatNumber(row.QueryQuantity)}</td>
                      <td className="num">{formatNumber(row.PayloadQuantity)}</td>
                      <td className="num">{formatNumber(row.TokensQuantity)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </SectionCard>
        </>
      )}

      {/* Subscriptions */}
      {activeTab === 'subscriptions' && (
        <SectionCard
          title="Subscriptions"
          subtitle="One subscription per CompanyAccount × pricing plan binding for this installation."
          flush
        >
          <div className="billing-admin__table-wrap">
            <table className="billing-admin__table">
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Plan</th>
                  <th>Cycle</th>
                  <th>Effective from</th>
                </tr>
              </thead>
              <tbody>
                {loading && Array.from({ length: 3 }).map((_, idx) => (
                  <tr key={`sk-${idx}`} className="billing-admin__skeleton-row">
                    {Array.from({ length: 4 }).map((__, c) => (
                      <td key={c}><span className="billing-admin__skeleton" /></td>
                    ))}
                  </tr>
                ))}
                {!loading && subs.length === 0 && (
                  <tr>
                    <td colSpan={4} style={{ padding: 0, border: 0 }}>
                      <EmptyState title="No subscriptions configured" message="Subscriptions are how an account is bound to a pricing plan and billing cycle. Add one from the onboarding wizard or directly via the API." />
                    </td>
                  </tr>
                )}
                {!loading && subs.map((s) => (
                  <tr key={s.SubscriptionId}>
                    <td className="billing-admin__table-cell-primary">{s.AccountName || s.CompanyAccountId}</td>
                    <td>{s.PricingPlanName || s.PricingPlanId}</td>
                    <td>
                      <span className="billing-admin__kind-chip">{s.BillingCycle || 'monthly'}</span>
                    </td>
                    <td>{formatDateOnly(s.EffectiveFrom)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>
      )}

      {/* Audit */}
      {activeTab === 'audit' && (
        <SectionCard
          title="Audit timeline"
          subtitle="All billing-relevant events for this installation, newest first."
        >
          {auditError && <Alert variant="error">{auditError}</Alert>}
          {!auditError && auditEvents === null && (
            <div className="billing-admin__skeleton" style={{ height: 220, borderRadius: 8 }} />
          )}
          {!auditError && auditEvents && auditEvents.length === 0 && (
            <EmptyState
              title="No audit events yet"
              message="Status changes, subscription updates, and disruption alerts will appear here as they happen."
            />
          )}
          {!auditError && auditEvents && auditEvents.length > 0 && (
            <ul className="billing-admin__audit-list">
              {auditEvents.map((ev) => {
                const id = ev.auditEventId || ev.AuditEventId || ev.id;
                const occurred = ev.occurredAt || ev.OccurredAt;
                const action = ev.action || ev.Action;
                const detail = ev.detail || ev.Detail;
                const actor = ev.actorName || ev.actorEmail || ev.ActorEmail || ev.ActorUserId;
                return (
                  <li key={id} className="billing-admin__audit-item">
                    <span className="billing-admin__audit-dot" />
                    <div className="billing-admin__audit-action">{actionLabel(action)}</div>
                    <div className="billing-admin__audit-meta">
                      {formatDate(occurred)}
                      {actor ? ` · ${actor}` : ''}
                    </div>
                    {detail && Object.keys(detail).length > 0 && (
                      <pre className="billing-admin__audit-detail">{JSON.stringify(detail, null, 2)}</pre>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </SectionCard>
      )}

      <Modal
        isOpen={showStatusModal}
        onClose={() => !statusBusy && setShowStatusModal(false)}
        title="Change installation status"
        size="medium"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowStatusModal(false)} disabled={statusBusy}>Cancel</Button>
            <Button
              variant={statusForm.status === 'disabled' ? 'danger' : 'primary'}
              onClick={submitStatus}
              disabled={statusBusy}
            >
              {statusBusy ? 'Saving…' : 'Confirm change'}
            </Button>
          </>
        }
      >
        <p className="billing-admin__muted" style={{ marginTop: 0 }}>
          Disabling this installation will return <code>402 Payment Required</code> on every billable
          request, both immediately on the control plane and after the next license refresh on the
          customer cloud.
        </p>
        {statusError && <Alert variant="error">{statusError}</Alert>}
        <div style={{ display: 'grid', gap: 12, marginTop: 12 }}>
          <Select
            variant="modal"
            label="New status"
            value={statusForm.status}
            onChange={(e) => setStatusForm({ ...statusForm, status: e.target.value })}
            options={STATUS_OPTIONS}
            placeholder=""
          />
          <Textarea
            variant="modal"
            label="Reason"
            hint="Surfaced in the audit log and billing webhooks."
            rows={3}
            value={statusForm.reason}
            onChange={(e) => setStatusForm({ ...statusForm, reason: e.target.value })}
            placeholder="Customer paused, payment failed, etc."
          />
          <TextInput
            variant="modal"
            label="Schedule for (optional)"
            hint="Leave blank to apply immediately."
            type="datetime-local"
            value={statusForm.scheduledAt}
            onChange={(e) => setStatusForm({ ...statusForm, scheduledAt: e.target.value })}
          />
        </div>
      </Modal>
    </div>
  );
}
